/**
 * @fileoverview Confirms a maker's withdrawal of their own pending prospect submission.
 * @description Withdrawing cancels the submission's review (POST
 * /sales/prospects/{id}/withdraw, with the submission's current ETag; see useWithdrawProspect);
 * the bank and its registration are kept. The parent mounts it only while a submission is being
 * withdrawn, so every opening starts with a fresh mutation. Close is held while the request is in flight, and
 * the confirmation runs from the mutation itself (so it survives an unmount). A failure stays
 * visible in the dialog.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Anshul Rokde
 */

import { useTranslation } from 'react-i18next'

import { useProspectDetails } from '@/features/sales/hooks/useProspectDetails'
import { useRefreshSalesQueries } from '@/features/sales/hooks/useRefreshSalesQueries'
import { useWithdrawProspect } from '@/features/sales/hooks/useWithdrawProspect'
import { requestErrorMessage } from '@/features/sales/lib/request-error.utils'
import { isStaleVersionError } from '@/features/sales/lib/stale-version.utils'
import Modal from '@/shared/components/molecules/Modal'
import { useGuardedClose } from '@/shared/hooks/useGuardedClose'

import DialogActions from './DialogActions'
import StaleVersionNotice from './StaleVersionNotice'

import type { ApprovalListItem } from '@/features/sales/types/approval.types'

interface WithdrawProspectDialogProps {
  request: ApprovalListItem
  onClose: () => void
  onWithdrawn: (request: ApprovalListItem) => void
}

/**
 * @component
 * @param {WithdrawProspectDialogProps} props - The submission and the handlers.
 * @param {ApprovalListItem} props.request - The caller's pending submission.
 * @param {() => void} props.onClose - Called when the dialog is dismissed.
 * @param {(request: ApprovalListItem) => void} props.onWithdrawn - Called once it's withdrawn.
 */
function WithdrawProspectDialog({ request, onClose, onWithdrawn }: WithdrawProspectDialogProps) {
  const { t } = useTranslation()
  const details = useProspectDetails(request.prospectId)
  const withdraw = useWithdrawProspect({
    /**
     * @description Reports the withdrawal to the parent, even if this dialog has unmounted.
     */
    onSuccess: () => onWithdrawn(request),
  })
  const close = useGuardedClose(onClose, withdraw.isPending)
  const refreshSales = useRefreshSalesQueries()
  const loaded = details.data
  const isStale = withdraw.isError && isStaleVersionError(withdraw.error)

  /**
   * @description Withdraws the submission at the version shown; the mutation reports it.
   */
  const confirm = () => {
    if (!loaded) {
      return
    }
    withdraw.mutate({ prospectId: request.prospectId, etag: loaded.etag })
  }

  /**
   * @description Clears a stale refusal and reloads the submission's latest state for review.
   */
  const onRefresh = () => {
    withdraw.reset()
    refreshSales()
  }

  return (
    <Modal
      open
      onClose={close}
      data-testid="withdraw-prospect-dialog"
      title={t('sales.pipeline.withdraw.title')}
      subtitle={request.bankName}
    >
      <div className="flex flex-col gap-4">
        <p className="text-[13.5px] text-ink" data-testid="withdraw-prospect-message">
          {t('sales.pipeline.withdraw.prompt')}
        </p>
        {details.isError && (
          <p
            role="alert"
            className="text-[13px] text-danger"
            data-testid="withdraw-prospect-load-error"
          >
            {requestErrorMessage(details.error, t)}
          </p>
        )}
        {isStale && <StaleVersionNotice onRefresh={onRefresh} isRefreshing={details.isFetching} />}
        {withdraw.isError && !isStale && (
          <p role="alert" className="text-[13px] text-danger" data-testid="withdraw-prospect-error">
            {requestErrorMessage(withdraw.error, t)}
          </p>
        )}
        <DialogActions
          testIdPrefix="withdraw-prospect"
          cancelLabel={t('sales.pipeline.approve.cancel')}
          confirmLabel={t('sales.pipeline.withdraw.confirm')}
          onCancel={close}
          onConfirm={confirm}
          isLoading={withdraw.isPending}
          disabled={!details.isSuccess || details.isFetching || isStale}
        />
      </div>
    </Modal>
  )
}

export default WithdrawProspectDialog
